import {cn} from '../lib/utils'
import {Icon} from '@susu/ui/components'
import {memo, useCallback, useEffect, useRef, useState} from 'react'

export interface SelectOption {
  value: string
  label: string
}

interface SelectFieldProps {
  id: string
  label: string
  name: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  error?: string
  disabled?: boolean
  placeholder?: string
  'data-testid': string
}

export const SelectField = memo(function SelectField({
  id,
  label,
  name,
  value,
  options,
  onChange,
  error,
  disabled = false,
  placeholder,
  'data-testid': testId,
}: SelectFieldProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLUListElement>(null)
  const selectedOption = options.find((opt) => opt.value === value)

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      // Use composedPath() so clicks inside a shadow DOM are correctly
      // identified as "inside" the container (event.target is retargeted
      // to the shadow host when the listener is on document).
      const path = event.composedPath()
      if (containerRef.current && !path.includes(containerRef.current)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleToggle = useCallback(() => {
    if (disabled) return
    setIsOpen((prev) => !prev)
  }, [disabled])

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue)
      setIsOpen(false)
    },
    [onChange],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        setIsOpen((prev) => !prev)
        return
      }

      if (!isOpen) return

      const currentIndex = options.findIndex((opt) => opt.value === value)

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0
        onChange(options[nextIndex].value)
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1
        onChange(options[prevIndex].value)
      }
    },
    [disabled, isOpen, options, value, onChange],
  )

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full', isOpen && 'z-[120]')}
      data-testid={testId}
    >
      {/* Trigger button */}
      <button
        id={id}
        type="button"
        role="combobox"
        name={name}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${id}-listbox`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full border bg-background pt-8 pr-12 pb-10 pl-[19px] text-left text-base leading-base font-light text-text transition-colors',
          'flex items-center justify-between',
          !isOpen && 'rounded-md',
          isOpen && 'rounded-t-md',
          error && 'border-border-error',
          disabled && 'cursor-default border-border-disabled text-text-disabled',
          !disabled && !error && 'hover:border-border-pressed',
          !disabled && !error && isOpen && 'rounded-t-md border-border-selected',
          !disabled && !error && !isOpen && 'border-border',
        )}
      >
        <span className={cn(!selectedOption && 'text-text-placeholder')}>
          {selectedOption?.label || placeholder || ''}
        </span>
        <Icon
          name="chevron-down"
          size={20}
          color="text"
          className={cn(
            'mt-4 transition-transform',
            isOpen && 'rotate-180',
            error && 'text-text-error',
            disabled && 'text-text-disabled',
          )}
        />
      </button>

      {/* Floating label */}
      <label
        htmlFor={id}
        className={cn(
          'pointer-events-none absolute -top-8 left-10 bg-background px-10 text-xs leading-xs font-light text-text-placeholder transition-all',
          error && 'text-text-error',
          disabled && 'text-text-disabled',
        )}
      >
        {label}
      </label>

      {/* Dropdown list */}
      {isOpen && (
        <ul
          ref={listboxRef}
          id={`${id}-listbox`}
          role="listbox"
          aria-labelledby={id}
          className="left-0 right-0 shadow-lg absolute top-44 z-[120] max-h-[180px] overflow-y-auto rounded-b-md border-x-1 border-b-1 border-border bg-surface"
        >
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={value === option.value}
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'flex w-full items-center justify-between py-8 pr-16 pl-20 text-left text-base leading-base font-light text-text transition-colors',
                  'hover:bg-surface-secondary',
                  value === option.value && 'bg-surface-secondary',
                )}
              >
                <span>{option.label}</span>
                {value === option.value && <Icon name="checkmark" size={20} color="text" />}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Error message */}
      {error && (
        <span id={`${id}-error`} className="block pt-8 text-xs leading-xs text-text-error">
          {error}
        </span>
      )}
    </div>
  )
})
